import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Base64 } from './base64';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import * as express from './express-ssr';
import { AppConfig } from './app-config';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@tytapp/environments/environment';

@Injectable()
export class Cookies {
    constructor(
        @Inject(PLATFORM_ID)
        private platformID : any,

        @Inject(REQUEST) @Optional()
        private request : express.ExpressRequest,
        @Inject(RESPONSE) @Optional()
        private response : express.ExpressResponse,

        private base64 : Base64,
        private appConfig : AppConfig
    ) {
    }

    public all() {
        let cookies = {};

        if (isPlatformServer(this.platformID)) {
            cookies = this.request.cookies || {}
        } else {
            cookies = {};

            document.cookie.split('; ').map(x => x.split('=', 2)).forEach(x => cookies[x[0]] = decodeURIComponent(x[1]));
        }

        return cookies;
    }

    public remove(names : string[], domain? : string) {
        if (isPlatformServer(this.platformID)) {
            let cookieSettings : any = { path: '/' };
            if (domain)
                cookieSettings.domain = domain;

            if (!this.response) {
                console.warn('Could not clear cookie(s): No response object available');
                return; 
            }

            if (this.response.headersSent) {
                console.warn('Could not clear cookie(s): Headers already sent.');
                return;
            }

            for (let name of names)
                this.response.clearCookie(name, cookieSettings);
        } else {
            for (let name of names)
                document.cookie = `${name}=; ${domain ? 'domain='+domain+'; ' : ''}expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`
        }
    }

    /**
     * Get a cookie
     * @param name 
     */
    public get<T>(name : string): T {
        let cookies = this.all();

        if (!cookies[name])
            return null;
        
        return JSON.parse(this.base64.decode(cookies[name]));
    }

    /**
     * Set a cookie
     * @param name 
     * @param value 
     * @param expires 
     */
    public set<T>(name : string, value : T, expires : number = null) {
        //console.log("Setting cookie "+name);
        
        let encodedValue = this.base64.encode(JSON.stringify(value));

        if (expires === null)
            expires = 1000*60*60*24*7;

        let expiryDate = new Date(Date.now() + expires);    

        let domain = environment.auth.domain;

        // Standard browser-side path 

        if (isPlatformBrowser(this.platformID)) {

            if (environment.environment !== 'production' && !window.location.href.includes(domain)) {
                alert(`Error: Tried to set cookie for domain '${domain}' but we are running from origin '${window.location.origin}'. This will not work. Are you on tytnetwork.com instead of tyt.com?`);
            }

            let cookieString = `${name}=${encodedValue}; path=/; ${domain ? 'domain='+domain+'; ' : ''}expires=${expiryDate.toUTCString()}`;
            document.cookie = cookieString;

            return;
        }

        // Special case for server-side 

        if (isPlatformServer(this.platformID)) {

            // If we don't have access to response, 
            // fail out, we cannot set a cookie.

            if (!this.response) {
                console.error('Missing HTTP response injectable while attempting to set cookie (issue with ngengine)');
                console.error('Request: '+(this.request ? 'Present' : 'Not Present'));
                console.error('Response: '+(this.response ? 'Present' : 'Not Present'));
                return;
            } 
            
            if (this.response.headersSent) {
                console.warn('Cannot set cookie, headers already sent.');
                return;
            }

            let opts : any = { path: '/', expires: expiryDate };
            if (domain)
                opts.domain = domain;
            
            this.response.cookie(name, encodedValue, opts);

            return;
        }

        throw new Error("No cookie provider available.");
    }
}